import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Chip,
  Drawer,
  FormControlLabel,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  Disclose,
  DrawerHead,
  Headline,
  Loading,
  Refusal,
} from "@lopie/kernel-ui";

/**
 * **This is no longer a screen.** It is where the roster half of
 * `People.jsx` lives: the editor, the seed lists and the source labels.
 * The screen that renders them is `People`, which joins this half to the
 * sign-in directory on the email address.
 *
 * Who gets told what.
 *
 * ## Why this is not the Access screen
 *
 * Access answers *who can see what*. This answers *who gets told things*, and
 * they are different populations on purpose. The person who should hear that
 * an agent blew its budget may have no login at all — they do not appear in
 * the sign-in pool, so no amount of widening Access would show them.
 *
 * ## Three ways somebody gets on this list, and the screen shows all three
 *
 * **Typed in.** An address and a name. No login required, which is the case
 * the kernel exists to hold.
 *
 * **From the sign-in pool.** People who can already log in — this org has
 * already trusted those addresses, so seeding from them is the cheapest
 * honest start.
 *
 * **From a connector.** And this one is a *pick*, never an import. This
 * kernel holds 46 `Person` records from Xero and every one is a counterparty
 * rather than an operator. A button that said "import all" would put another
 * company's accounts-payable contact on the list that receives this org's
 * spending alerts, so the screen lists candidates and somebody chooses.
 *
 * ## The figure is reach, not headcount
 *
 * A roster of twelve where nobody subscribed to anything tells you nothing.
 * The number at the top is how many people would actually be told if an agent
 * hit its limit right now, which is the question somebody opens this screen
 * with.
 */

const SOURCE_LABEL = {
  manual: "typed in",
  directory: "can sign in",
};

export function sourceOf(user) {
  if (user.source?.startsWith("connector:")) {
    return `from ${user.source.slice("connector:".length)}`;
  }
  return SOURCE_LABEL[user.source] || user.source || "unknown";
}

/** One person in full, and the only place a subscription is changed. */
export function UserDrawer({ user, topics, onClose, onSave, onRemove, busy }) {
  const [name, setName] = useState("");
  const [chosen, setChosen] = useState([]);
  const [active, setActive] = useState(true);

  useEffect(() => {
    setName(user?.name || "");
    setChosen(user?.topics || []);
    setActive(user?.active ?? true);
  }, [user?.email]);

  if (!user) return <Drawer anchor="right" open={false} onClose={onClose} />;

  const toggle = (topic) =>
    setChosen((held) =>
      held.includes(topic) ? held.filter((t) => t !== topic) : [...held, topic]);

  return (
    <Drawer
      anchor="right"
      open={Boolean(user)}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: { xs: "100%", sm: 560 }, maxWidth: "100%" } } }}
    >
      <Box sx={{ p: { xs: 2, sm: 3 }, minWidth: 0 }}>
        <DrawerHead
          title={user.name || user.email}
          id={user.name ? user.email : null}
          onClose={onClose}
          lede={
            <>
              What this person is told, and where they came from. Changing the
              list below changes what reaches them from the next event onward —
              it sends nothing now and it does not alter what they may see,
              which is the Access screen&apos;s question and a different one.
            </>
          }
        />

        <Stack spacing={3}>
          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              Tell them about
            </Typography>
            <Stack spacing={0.5}>
              {Object.entries(topics).map(([name_, detail]) => (
                <Box key={name_}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={chosen.includes(name_)}
                        disabled={busy}
                        onChange={() => toggle(name_)}
                      />
                    }
                    label={name_}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", ml: 4, mt: -0.5, maxWidth: "58ch" }}
                  >
                    {detail}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>

          <TextField
            size="small"
            label="Name"
            value={name}
            disabled={busy}
            onChange={(e) => setName(e.target.value)}
            helperText="What a person is called. Nothing is decided against it."
          />

          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={active}
                disabled={busy}
                onChange={(e) => setActive(e.target.checked)}
              />
            }
            label="On the roster"
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: -2, ml: 4, maxWidth: "58ch" }}>
            Switching this off stops them being told anything while keeping
            what they were subscribed to — which is different from removing
            them, and is the one to reach for when somebody is on leave.
          </Typography>

          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
            <Button
              variant="contained"
              size="small"
              disabled={busy}
              onClick={() => onSave({ ...user, name, topics: chosen, active })}
            >
              Save
            </Button>
            <Button
              size="small"
              color="error"
              disabled={busy || !onRemove}
              onClick={() => onRemove(user)}
            >
              Remove from roster
            </Button>
          </Stack>

          <Disclose label="Where this row came from">
            <Stack spacing={0.5}>
              <Typography variant="body2" color="text.secondary">
                {sourceOf(user)}
                {user.added_by ? `, added by ${user.added_by}` : ""}
                {user.added_at ? ` on ${user.added_at.slice(0, 10)}` : ""}.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {user.principal
                  ? `Signs in as ${user.principal}.`
                  : "No login. They receive notifications and cannot open anything, which is an ordinary state for an address that only needs telling."}
              </Typography>
            </Stack>
          </Disclose>
        </Stack>
      </Box>
    </Drawer>
  );
}

/** Candidates from somewhere else, to be picked from rather than imported. */
export function Seed({ title, note, candidates, onAdd, busy }) {
  if (!candidates.length) return null;
  return (
    <Disclose label={`${title} — ${candidates.length} to choose from`}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxWidth: "62ch" }}>
        {note}
      </Typography>
      <Stack spacing={0.5}>
        {candidates.map((c) => (
          <Stack
            key={c.email}
            direction="row"
            spacing={1}
            sx={{ alignItems: "baseline", justifyContent: "space-between",
                  flexWrap: "wrap", rowGap: 0.5, py: 0.5,
                  borderTop: 1, borderColor: "divider" }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
                {c.name || c.email}
              </Typography>
              {c.name ? (
                <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
                  {c.email}
                </Typography>
              ) : null}
            </Box>
            <Button size="small" disabled={busy} onClick={() => onAdd(c)}>
              Add
            </Button>
          </Stack>
        ))}
      </Stack>
    </Disclose>
  );
}

